import React from "react";
import { motion } from "framer-motion";
import { MapPin, ArrowUpRight, Check } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";

export const Distribution = () => {
  const { t } = useLang();
  return (
    <section
      id="wholesale"
      className="relative bg-brand-cream py-24 md:py-32"
      data-testid="distribution-section"
    >
      <div className="max-w-[1600px] mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="grid grid-cols-12 gap-8 items-end mb-16 md:mb-20"
        >
          <div className="col-span-12 md:col-span-8">
            <div className="flex items-center gap-3 mb-6">
              <span className="block w-8 h-px bg-brand-red" />
              <span className="font-sans text-[11px] tracking-[0.3em] uppercase text-brand-red">
                {t.distribution.label}
              </span>
            </div>
            <h2 className="font-serif text-4xl md:text-6xl lg:text-7xl text-brand-ink leading-[0.95]">
              {t.distribution.headline}
              <br />
              <span className="italic text-brand-red">{t.distribution.headline2}</span>
            </h2>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-brand-ink/15 border border-brand-ink/15">
          {t.distribution.tiers.map((tier, i) => (
            <motion.div
              key={tier.id}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.8, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] }}
              className="relative bg-brand-cream p-8 md:p-10 group hover:bg-brand-cream-alt transition-colors duration-500"
              data-testid={`distribution-tier-${tier.id}`}
            >
              <div className="flex items-start justify-between mb-10">
                <span className="outline-number text-7xl md:text-8xl leading-none pointer-events-none">
                  0{i + 1}
                </span>
                <MapPin className="w-5 h-5 text-brand-red" />
              </div>

              <h3 className="font-serif text-2xl md:text-3xl text-brand-ink mb-4 leading-tight">
                {tier.title}
              </h3>
              <p className="font-sans text-sm text-brand-muted leading-relaxed">{tier.desc}</p>

              <ul className="mt-6 space-y-2">
                {tier.highlights.map((h, j) => (
                  <li key={j} className="flex items-start gap-2 font-sans text-sm text-brand-ink">
                    <Check className="w-4 h-4 text-brand-red shrink-0 mt-0.5" />
                    <span>{h}</span>
                  </li>
                ))}
              </ul>

              <a
                href="#contact"
                className="mt-8 inline-flex items-center gap-2 font-sans text-xs tracking-[0.2em] uppercase text-brand-red group/cta"
                data-testid={`distribution-cta-${tier.id}`}
              >
                <span className="border-b border-brand-red pb-1">{t.distribution.ctaLabel}</span>
                <ArrowUpRight className="w-3.5 h-3.5 group-hover/cta:translate-x-1 group-hover/cta:-translate-y-1 transition-transform" />
              </a>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
